package com.example.event_management_system.data.repository

import com.example.event_management_system.data.models.Event
import com.example.event_management_system.util.currentTimeMillis
import dev.gitlive.firebase.firestore.FirebaseFirestore
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.map

class EventRepository(
    private val firestore: FirebaseFirestore
) : IEventRepository {
    private val eventsCollection = firestore.collection("events")

    override fun getEventsFlow(locationId: String): Flow<List<Event>> {
        return eventsCollection
            .where { "locationId" equalTo locationId }
            .where { "isDeleted" equalTo false }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { doc ->
                    doc.data<Event>().copy(id = doc.id)
                }
            }
    }

    override fun getEventsByMonthFlow(
        locationId: String,
        startOfMonth: Long,
        endOfMonth: Long
    ): Flow<List<Event>> {
        return eventsCollection
            .where { "locationId" equalTo locationId }
            .where { "isDeleted" equalTo false }
            .where { "eventDate" greaterThanOrEqualTo startOfMonth.toDouble() }
            .where { "eventDate" lessThan endOfMonth.toDouble() }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { doc ->
                    doc.data<Event>().copy(id = doc.id)
                }
            }
    }

    override fun getAllEventsFlow(): Flow<List<Event>> {
        return eventsCollection
            .where { "isDeleted" equalTo false }
            .snapshots
            .map { snapshot ->
                snapshot.documents.map { doc ->
                    doc.data<Event>().copy(id = doc.id)
                }
            }
    }

    override suspend fun getEvent(eventId: String): Event? {
        return try {
            val doc = eventsCollection.document(eventId).get()
            doc.data<Event>().copy(id = doc.id)
        } catch (e: Throwable) {
            null
        }
    }

    override suspend fun createEvent(event: Event): String {
        val currentTime = currentTimeMillis()
        val eventWithTimestamps = event.copy(
            createdAt = currentTime,
            updatedAt = currentTime
        )
        val docRef = eventsCollection.add(eventWithTimestamps)
        return docRef.id
    }

    override suspend fun updateEvent(eventId: String, event: Event) {
        val currentTime = currentTimeMillis()
        val eventWithTimestamp = event.copy(updatedAt = currentTime)
        eventsCollection.document(eventId).set(eventWithTimestamp)
    }

    override suspend fun deleteEvent(eventId: String) {
        val currentTime = currentTimeMillis()
        eventsCollection.document(eventId).update(
            "isDeleted" to true,
            "updatedAt" to currentTime.toDouble()
        )
    }

    override suspend fun hardDeleteEvent(eventId: String) {
        eventsCollection.document(eventId).delete()
    }
}
